import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Upload, ImageIcon } from "lucide-react";
import { uploadToCloudinary } from "@/lib/upload";

export default function ProductsTab({ onReload }) {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const p = await api.get("/products");
      setProducts(p.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (p) => {
    if (!window.confirm(`Hapus ${p.name}?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produk dihapus");
      load();
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateStock = async (p, stock) => {
    try {
      await api.put(`/admin/products/${p.id}`, { stock: Number(stock) });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="products-tab">
      <div className="flex justify-end">
        <button
          data-testid="add-product-button"
          onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] text-sm"
        >
          <Plus size={16} /> Tambah Produk
        </button>
      </div>

      <div className="border border-stone-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-stone-50 text-left">
            <tr>
              <th className="px-4 py-3 overline text-stone-500">Produk</th>
              <th className="px-4 py-3 overline text-stone-500">Slug</th>
              <th className="px-4 py-3 overline text-stone-500">Foto</th>
              <th className="px-4 py-3 overline text-stone-500">Stok</th>
              <th className="px-4 py-3 overline text-stone-500">Featured</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-stone-200" data-testid={`product-row-${p.slug}`}>
                <td className="px-4 py-3 flex items-center gap-3">
                  <img src={p.image_url} alt="" className="h-12 w-12 object-cover" />
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-stone-500">{p.tagline}</div>
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-600">{p.slug}</td>
                <td className="px-4 py-3">{(p.images || []).length || 1}</td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    defaultValue={p.stock || 0}
                    data-testid={`stock-input-${p.slug}`}
                    onBlur={(e) => {
                      if (Number(e.target.value) !== p.stock) updateStock(p, e.target.value);
                    }}
                    className="w-24 h-9 px-2 border border-stone-300 focus:border-[#F39237] outline-none"
                  />
                </td>
                <td className="px-4 py-3">{p.featured ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button
                    data-testid={`edit-product-${p.slug}`}
                    onClick={() => setEditing(p)}
                    className="p-2 text-stone-600 hover:text-[#F39237]"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    data-testid={`delete-product-${p.slug}`}
                    onClick={() => del(p)}
                    className="p-2 text-stone-600 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-stone-500">
                  Belum ada produk.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            onReload && onReload();
          }}
        />
      )}
    </div>
  );
}

function ProductModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    name: initial.name || "",
    slug: initial.slug || "",
    tagline: initial.tagline || "",
    description: initial.description || "",
    fabric: initial.fabric || "",
    sizes: (initial.sizes || []).join(", "),
    colors: (initial.colors || []).join(", "),
    stock: initial.stock ?? 0,
    image_url: initial.image_url || "",
    images: initial.images || [],
    featured: !!initial.featured,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
        stock: Number(form.stock) || 0,
        image_url: form.image_url || (form.images[0] || ""),
      };
      if (isEdit) {
        await api.put(`/admin/products/${initial.id}`, payload);
      } else {
        await api.post(`/admin/products`, payload);
      }
      toast.success(isEdit ? "Produk diperbarui" : "Produk ditambahkan");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of Array.from(files).slice(0, 8 - form.images.length)) {
        const url = await uploadToCloudinary(f, "image", "aurora/products");
        urls.push(url);
      }
      setForm((prev) => ({
        ...prev,
        images: [...prev.images, ...urls].slice(0, 8),
        image_url: prev.image_url || urls[0] || "",
      }));
      toast.success(`${urls.length} foto terupload`);
    } catch (e) {
      toast.error("Upload gagal: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  const removeImg = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-stone-200 sticky top-0 bg-white z-10">
          <h3 className="font-display text-2xl">{isEdit ? "Edit Produk" : "Tambah Produk"}</h3>
          <button onClick={onClose} data-testid="close-modal" className="p-2"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Nama" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="pf-name" required />
            <Input label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="pf-slug" required />
          </div>
          <Input label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testId="pf-tagline" />
          <Input label="Deskripsi" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="pf-description" textarea required />
          <Input label="Bahan" value={form.fabric} onChange={(v) => setForm({ ...form, fabric: v })} testId="pf-fabric" />
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Ukuran (pisah koma)" value={form.sizes} onChange={(v) => setForm({ ...form, sizes: v })} testId="pf-sizes" />
            <Input label="Warna (pisah koma)" value={form.colors} onChange={(v) => setForm({ ...form, colors: v })} testId="pf-colors" />
          </div>
          <Input label="Stok (pcs)" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testId="pf-stock" />

          <div>
            <label className="block overline text-stone-500 mb-2">Foto Produk (maks 8)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.images.map((url, idx) => (
                <div key={idx} className="relative w-20 h-24 border border-stone-200 overflow-hidden group">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImg(idx)}
                    className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-img-${idx}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {form.images.length < 8 && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="pf-upload-btn"
                  className="w-20 h-24 border-2 border-dashed border-stone-300 flex flex-col items-center justify-center gap-1 hover:border-[#F39237] hover:text-[#F39237] transition-colors text-stone-500 text-xs"
                >
                  {uploading ? <Upload size={16} className="animate-pulse" /> : <ImageIcon size={16} />}
                  {uploading ? "Upload" : "+ Foto"}
                </button>
              )}
              <input
                type="file"
                ref={fileRef}
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => onFiles(e.target.files)}
                data-testid="pf-file-input"
              />
            </div>
            <p className="text-xs text-stone-500">Foto pertama otomatis jadi cover.</p>
          </div>

          <Input
            label="Atau URL Cover (opsional)"
            value={form.image_url}
            onChange={(v) => setForm({ ...form, image_url: v })}
            testId="pf-image-url"
          />

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="pf-featured"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            />
            Featured (best seller)
          </label>

          <div className="flex gap-3 pt-3 border-t border-stone-200">
            <button type="button" onClick={onClose} className="px-5 py-3 border border-stone-300" data-testid="pf-cancel">
              Batal
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              data-testid="pf-submit"
              className="px-5 py-3 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] disabled:opacity-60"
            >
              {saving ? "Menyimpan…" : isEdit ? "Simpan" : "Tambah"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, textarea, testId }) {
  return (
    <div>
      <label className="block overline text-stone-500 mb-2">{label}</label>
      {textarea ? (
        <textarea
          data-testid={testId}
          required={required}
          value={value}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 bg-transparent border border-stone-300 focus:border-[#F39237] outline-none"
        />
      ) : (
        <input
          type={type}
          data-testid={testId}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 px-3 bg-transparent border border-stone-300 focus:border-[#F39237] outline-none"
        />
      )}
    </div>
  );
}
